"use client";

import type { ReactNode } from "react";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { Item } from "@/types";
import { imageFitClass, itemDisplay } from "../itemDisplay";
import { useHoveredItemId } from "../hooks/useHoveredItemId";
import { useItems } from "../hooks/useItems";
import { useSelectedItemId } from "../hooks/useSelectedItemId";
import { PoiCardItem } from "./PoiCardItem";
import { PoiListItem } from "./PoiListItem";

/**
 * The rendered result rows, as a list or as cards.
 *
 * The legacy `ListView` and `CardView` were the same component twice over —
 * identical queries, identical title/subtitle/image derivation, identical
 * hover and selection wiring — differing only in which item component they
 * rendered. That difference is a prop here.
 */
export function ResultViews({
  viewType,
  onItemClick,
}: {
  viewType: "list" | "cards";
  onItemClick: (item: Item) => void;
}) {
  const configs = useProjectConfigs();
  const media = useMediaContext();
  const { data } = useItems();
  const { selectedItemId } = useSelectedItemId();
  const hoveredItemId = useHoveredItemId((state) => state.hoveredItemId);
  const setHoveredItemId = useHoveredItemId((state) => state.setHoveredItemId);

  const items = data?.features ?? [];
  const isCards = viewType === "cards";
  const ItemComponent = isCards ? PoiCardItem : PoiListItem;
  const fitClass = imageFitClass(configs.poiMainImageFit);

  // Cards lay out two across; rows stack. Kept on an inner element rather than
  // merged onto the list wrapper, because `display: flex` and `display: grid`
  // on one element resolve by stylesheet order, not class order.
  const Wrapper = isCards ? CardGrid : Rows;

  return (
    <Wrapper>
      {items.map((item) => {
        const display = itemDisplay(item, configs, media);

        return (
          <ItemComponent
            key={item.id}
            title={display.title}
            subtitle={display.subtitle}
            imageUrl={display.imageUrl}
            imageFitClass={fitClass}
            isActive={item.id === hoveredItemId || item.id === selectedItemId}
            onSelect={() => onItemClick(item)}
            onHoverOn={() => setHoveredItemId(item.id)}
            onHoverOff={() => setHoveredItemId(null)}
          />
        );
      })}
    </Wrapper>
  );
}

function Rows({ children }: { children: ReactNode }) {
  return <div className="items flex flex-col">{children}</div>;
}

function CardGrid({ children }: { children: ReactNode }) {
  return (
    <div className="items flex flex-col">
      <div className="card-view grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
